import { CSSResultGroup, html, LitElement, nothing, type PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { map } from 'lit/directives/map.js'
import { CSEStepperInput } from '@cloudsuite/mosaic-elements'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import {
  ProductOptionsChangedEvent,
  ProductQuantityChangedEvent,
  ProductSelectionRequestedEvent,
} from 'components/discovery/pdp/shared/pdp-selection-events.js'
import {
  GetProductOptionsRequest,
  productOptionsApi,
  productOptionsPath,
  type ProductOption,
} from 'components/discovery/pdp/product-options/api/product-options-api.js'
import 'components/shared/product-preview/product-preview.js'
import { HeadingSlotMixin } from 'components/shared/heading/heading-slot-mixin.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import { style } from './product-options.style.js'


export const productOptionsHiddenFields = ['image'] as const

const withUom = (quantity: number, option: ProductOption): string => {
  const uom = option.product_data.ordering?.default_uom
  return uom ? `${quantity} ${uom}` : `${quantity}`
}

const stopPropagation = (event: Event) => event.stopPropagation()

const formatAmount = (amount: number, option: ProductOption, locale: string): string => {
  const currency = option.product_data.prices?.currency_code

  if (!currency) return Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)

  return Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount)
}

@customElement('csc-product-options')
export class CSCProductOptions extends HiddenFieldsMixin(HeadingSlotMixin(LitElement), productOptionsHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Number, attribute: 'product-id' })
  productId: number | null = null

  /** @deprecated Ignored: the options URL is built from `product-id`. */
  @property({ type: String })
  set endpoint(_value: string) {
    console.warn('<csc-product-options> "endpoint" is deprecated and ignored, the options URL is built from "product-id".')
  }

  get endpoint() {
    return ''
  }

  @property()
  locale = 'en-US'

  @state()
  private productOptions: ProductOption[] = []

  @state()
  private productQuantity = 0

  @state()
  private selectedOptionIds: ReadonlySet<number> = new Set()

  @state()
  private optionQuantities: ReadonlyMap<number, number> = new Map()

  private latestRequest = 0

  private fetching: Promise<void> = Promise.resolve()

  override connectedCallback() {
    super.connectedCallback()
    window.addEventListener(ProductQuantityChangedEvent.type, this.receiveProductQuantity)
    window.addEventListener(ProductSelectionRequestedEvent.type, this.answerSelectionRequest)
    if (!this.hasUpdated) return

    this.requestProductQuantity()
    this.fetching = this.fetchProductOptions()
  }

  override disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener(ProductQuantityChangedEvent.type, this.receiveProductQuantity)
    window.removeEventListener(ProductSelectionRequestedEvent.type, this.answerSelectionRequest)
  }

  private requestProductQuantity = () => {
    if (this.productId) this.dispatchEvent(new ProductSelectionRequestedEvent(this.productId))
  }

  private receiveProductQuantity = (event: ProductQuantityChangedEvent) => {
    if (event.productId === this.productId) this.productQuantity = event.quantity
  }

  private answerSelectionRequest = (event: ProductSelectionRequestedEvent) => {
    if (event.productId === this.productId && event.composedPath()[0] !== this) this.publishSelectedOptions()
  }

  override willUpdate(changedProperties: PropertyValues) {
    if (changedProperties.has('productId')) {
      this.requestProductQuantity()
      this.fetching = this.fetchProductOptions()
    }
  }

  override async getUpdateComplete(): Promise<boolean> {
    await super.getUpdateComplete()
    await this.fetching
    return super.getUpdateComplete()
  }

  override firstUpdated() {
    this.publishSelectedOptions()
  }

  private async optionsUrl(): Promise<string> {
    if (!this.productId) return ''
    return (await createUrl(productOptionsPath(this.productId))).href
  }

  private fetchProductOptions = async (): Promise<void> => {
    const request = ++this.latestRequest

    try {
      const url = await this.optionsUrl()
      if (!this.isConnected || request !== this.latestRequest || !url) return

      const productOptions = await productOptionsApi.getProductOptions(new GetProductOptionsRequest(url))
      if (!this.isConnected || request !== this.latestRequest) return

      this.setProductOptions(productOptions)
    } catch (error) {
      console.error('Retrieving the product options failed', error)
    }
  }

  private setProductOptions = (productOptions: ProductOption[]) => {
    this.productOptions = productOptions
    this.selectedOptionIds = new Set(
      this.productOptions.filter(option => option.selected).map(option => option.id)
    )
    this.optionQuantities = new Map(
      this.productOptions.map(option => [option.id, option.quantity || 1])
    )
    this.publishSelectedOptions()
  }

  private toggleOption = (option: ProductOption) => {
    const selectedOptionIds = new Set(this.selectedOptionIds)

    if (selectedOptionIds.has(option.id)) {
      selectedOptionIds.delete(option.id)
    } else {
      selectedOptionIds.add(option.id)
    }

    this.selectedOptionIds = selectedOptionIds
    this.publishSelectedOptions()
  }

  private changeOptionQuantity = (option: ProductOption, event: Event) => {
    const stepper = event.currentTarget as CSEStepperInput
    const quantity = parseFloat(stepper.value)
    if (!Number.isFinite(quantity) || quantity <= 0) return

    this.optionQuantities = new Map(this.optionQuantities).set(option.id, quantity)
    this.publishSelectedOptions()
  }

  private mainQuantity = (): number => this.productQuantity > 0 ? this.productQuantity : 1

  private quantityForOption = (option: ProductOption): number => {
    if (option.type === 'service') return 1

    return this.optionQuantities.get(option.id) ?? option.quantity ?? 1
  }

  private totalQuantityForOption = (option: ProductOption): number => {
    if (option.type === 'goods') return this.quantityForOption(option)

    return this.quantityForOption(option) * this.mainQuantity()
  }

  private publishSelectedOptions = () => {
    if (!this.productId) return

    this.dispatchEvent(new ProductOptionsChangedEvent(
      this.productId,
      this.productOptions
        .filter(option => this.selectedOptionIds.has(option.id))
        .map(option => ({ id: option.id, quantity: this.quantityForOption(option) }))
    ))
  }

  private renderQuantityStepper = (option: ProductOption) => html`
    <div class="option-quantity" @click=${stopPropagation}>
      <cse-stepper-input
        value="${this.quantityForOption(option)}"
        @change=${(event: Event) => this.changeOptionQuantity(option, event)}
      ></cse-stepper-input>
      ${option.type === 'goods_as_part_of_main_product' ? html`
        <cse-text variant="body-s">${t('per_product', { ns: 'csc-product-options', default: 'Per product' })}</cse-text>
      ` : nothing}
    </div>
  `

  private renderTrailing = (option: ProductOption, selected: boolean) => {
    if (option.type === 'service') {
      return html`
        <cse-text class="option-quantity"><strong>${withUom(this.mainQuantity(), option)}</strong></cse-text>
      `
    }

    return selected ? this.renderQuantityStepper(option) : nothing
  }

  private renderMultiplication = (option: ProductOption) => html`
    ${t('quantity_multiplication', {
      ns: 'csc-product-options',
      default: '{{variable_per_product}} x {{variable_main_quantity}} =',
      variable_per_product: this.quantityForOption(option),
      variable_main_quantity: this.mainQuantity(),
    })}
    <strong>${withUom(this.totalQuantityForOption(option), option)}</strong>
  `

  private renderSummaryContent = (option: ProductOption) => {
    if (option.type === 'goods') return html`<strong>${withUom(this.totalQuantityForOption(option), option)}</strong>`

    return this.renderMultiplication(option)
  }

  private renderSummary = (option: ProductOption) => html`
    <cse-divider orientation="horizontal"></cse-divider>
    <div class="option-calculation">
      <cse-text class="option-summary">${this.renderSummaryContent(option)}</cse-text>
      <cse-text class="option-total">
        <strong>${formatAmount(option.price * this.totalQuantityForOption(option), option, this.locale)}</strong>
      </cse-text>
    </div>
  `

  private renderOption = (option: ProductOption) => {
    const selected = this.selectedOptionIds.has(option.id)

    return html`
      <cse-selectable
        size="small"
        fit="fill"
        state="${selected ? 'selected' : 'selectable'}"
        @click=${() => this.toggleOption(option)}
      >
        <div class="option">
          <cse-checkbox inert .checked=${selected}></cse-checkbox>
          <csc-product-preview
            product-name=${option.name}
            image-source=${option.product_data.default_picture?.mini}
            .price=${option.price_formatted}
            .uom=${option.product_data.ordering?.default_uom}
            hidden-fields=${this.isHidden('image') ? 'image' : nothing}
          ></csc-product-preview>
          ${this.renderTrailing(option, selected)}
          ${selected ? this.renderSummary(option) : nothing}
        </div>
      </cse-selectable>
    `
  }

  override render() {
    if (this.productOptions.length <= 0) return nothing

    return html`
      <div class="product-options">
        ${this.renderHeadingSlot()}
        <div class="options">
          ${map(this.productOptions, this.renderOption)}
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-product-options': CSCProductOptions
  }
}
